import type { ExperimentWithVerdict } from '@@/api/experiments/implementation-verdict';
import { Dialog, DialogContent, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';

type EndOnImplementDialogProps = {
  /** The row being marked implemented. Null keeps the dialog closed. */
  experiment: ExperimentWithVerdict | null;
  onResolve: (params: { end: boolean }) => void;
  onDismiss: () => void;
};

/**
 * Asked when a Running or Deployed experiment is marked implemented. Once the change is
 * built into the site, whatever Pertento is still serving applies it again on top.
 */
export const EndOnImplementDialog = ({ experiment, onResolve, onDismiss }: EndOnImplementDialogProps) => {
  const isDeployed = experiment?.status === 'Deployed';

  return (
    <Dialog
      open={!!experiment}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
    >
      <DialogContent className="bg-panel border-line rounded-panel border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lede font-semibold tracking-tight">End this experiment too?</DialogTitle>
          <DialogDescription className="supporting">
            <span className="text-foreground font-medium">{experiment?.name}</span> is still{' '}
            {isDeployed ? 'deployed' : 'running'}. If the change is now part of your site, ending it stops Pertento
            {isDeployed ? ' serving the deployed variant' : ' splitting traffic'} so the change is not applied twice. It
            is marked implemented either way.
          </DialogDescription>
        </DialogHeader>
        {/* Both choices mark it, so the buttons name only what differs. Cancelling is the
            corner close and Escape, which the dialog already has. */}
        <DialogFooter className="gap-2 sm:space-x-0">
          <button
            type="button"
            onClick={() => onResolve({ end: false })}
            className="border-line text-dim hover:text-foreground hover:bg-hover flex h-8 items-center justify-center whitespace-nowrap rounded-lg border bg-transparent px-3 text-meta transition-colors duration-150"
          >
            {isDeployed ? 'Keep deployed' : 'Keep running'}
          </button>
          <button
            type="button"
            onClick={() => onResolve({ end: true })}
            className="bg-cta hover:bg-ctahover flex h-8 items-center justify-center whitespace-nowrap rounded-lg px-3 text-meta font-medium text-white transition-colors duration-150"
          >
            End experiment
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
